import React, { useState, useCallback, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Checkbox,
  FormControlLabel,
  ButtonGroup,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Clear as ClearIcon,
  Casino as CasinoIcon,
  AccountBalanceWallet as WalletIcon,
  Delete as DeleteIcon,
  Shuffle as ShuffleIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import axios from 'axios';

interface CounterBet {
  code: string;
  bigAmount: number;
  smallAmount: number;
  fourAAmount: number;
  aAmount: number;
  abcAmount: number;
  fiveDAmount: number;
  sixDAmount: number;
}

interface BetEntry {
  id: number;
  gameType: string;
  numbers: string[];
  betType: string;
  optionType: string; // renamed from betPlayType for API naming consistency
  bigAmount: number;
  smallAmount: number;
  fourAAmount: number;
  aAmount: number;
  abcAmount: number;
  fiveDAmount: number;
  sixDAmount: number;
  dates: string[];
  operatorName: string; // Simplified: use operator name instead of gameId/drawId
  counterBets: CounterBet[];
}



interface GameTypeConfig {
  name: string;
  code: string;
  numberCount: number;
  numberRange: [number, number];
  color: string;
  description: string;
}

const gameTypes: GameTypeConfig[] = [
  { name: '4D', code: '4D', numberCount: 4, numberRange: [0, 9], color: '#e91e63', description: 'Pick 4 digits' }
];

const betTypes = [
  { value: 'Big', label: 'Big', multiplier: 1, description: 'Big forecast bet' },
  { value: 'Small', label: 'Small', multiplier: 0.6, description: 'Small forecast bet' },
  { value: '4A', label: '4A', multiplier: 1.2, description: '4A bet type' },
  { value: 'A', label: 'A', multiplier: 0.8, description: 'A bet type' },
  { value: 'ABC', label: 'ABC', multiplier: 1.5, description: 'ABC bet type' },
  { value: '5D', label: '5D', multiplier: 2.0, description: '5D bet type' },
  { value: '6D', label: '6D', multiplier: 3.0, description: '6D bet type' }
];

// Canonical labels for option type display
const optionTypeLabels: Record<string, string> = {
  single: 'Single',
  ibox: 'iBox',
  box: 'Box',
  reverse: 'Reverse'
};

const lotteryCounters = [
  { name: 'Magnum 4D', code: 'MG', color: '#f44336' },
  { name: 'Damacai 4D', code: 'DC', color: '#4caf50' },
  { name: 'Singapore 4D', code: 'SG', color: '#2196f3' },
  { name: 'Sports Toto', code: 'ST', color: '#ff9800' },
  { name: 'Cash Sweep', code: 'CS', color: '#9c27b0' },
  { name: 'Sabah 88', code: 'S88', color: '#795548' },
  { name: 'STC 4D', code: 'STC', color: '#607d8b' },
  { name: 'Grand Dragon', code: 'GD', color: '#e91e63' },
  { name: '9 Lotto', code: '9L', color: '#009688' },
];

const EnhancedBetPage: React.FC = () => {
  console.log('🎯 EnhancedBetPage component is mounting/rendering');
  
  const [selectedGameType, setSelectedGameType] = useState<GameTypeConfig>(gameTypes[0]);
  const [numberInput, setNumberInput] = useState<string>('');
  const [selectedBetType, setSelectedBetType] = useState('BOTH');
  const [optionType, setOptionType] = useState<string>('single');
  const [bigAmount, setBigAmount] = useState<number>(0);
  const [smallAmount, setSmallAmount] = useState<number>(0);
  const [fourAAmount, setFourAAmount] = useState<number>(0);
  const [aAmount, setAAmount] = useState<number>(0);
  const [abcAmount, setAbcAmount] = useState<number>(0);
  const [fiveDAmount, setFiveDAmount] = useState<number>(0);
  const [sixDAmount, setSixDAmount] = useState<number>(0);
  const [selectedOperators, setSelectedOperators] = useState<number[]>([]);
  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  const [availableOperatorIds, setAvailableOperatorIds] = useState<Set<number>>(new Set());

  // Helper function to determine required number length based on bet amounts
  const getRequiredNumberLength = (): number => {
    // If 6D has amount, require 6 digits
    if (sixDAmount > 0) return 6;
    // If 5D has amount, require 5 digits
    if (fiveDAmount > 0) return 5;
    // If A or ABC has amount, allow 3 digits
    if (aAmount > 0 || abcAmount > 0) return 3;
    // Otherwise default to 4 digits for standard bets
    return 4;
  };

  // Helper function to format date as YYYY-MM-DD in local timezone
  const formatLocalDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Get today's date string in local timezone
  const getTodayString = (): string => {
    return formatLocalDate(new Date());
  };

  // Generate next 7 days starting from today (using local timezone)
  const getNext7Days = () => {
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date();
      date.setDate(date.getDate() + i);
      dates.push(formatLocalDate(date));
    }
    return dates;
  };

  const availableDates = getNext7Days();
  const [betEntries, setBetEntries] = useState<BetEntry[]>([]);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  console.log('👤 User from useAuth:', user ? 'LOGGED_IN' : 'NOT_LOGGED_IN');
  console.log('🔧 QueryClient:', queryClient ? 'AVAILABLE' : 'MISSING');

  // Fetch operators from the Operator table
  const { data: operatorsData, isLoading: isLoadingOperators, error: operatorsError } = useQuery({
    queryKey: ['operators'],
    queryFn: async () => {
      console.log('🚀 Making API call to fetch operators');
      try {
        const response = await axios.get('/operator');
        console.log('✅ Operators API response received:', response.data);
        return response.data as { id: number; name: string; code: string; createdAt: string }[];
      } catch (error) {
        console.error('❌ Operators API call failed:', error);
        throw error;
      }
    },
    refetchInterval: 300000, // Refresh every 5 minutes (operators change less frequently)
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 401) {
        return false;
      }
      return failureCount < 3;
    },
    enabled: true
  });

  // Fetch available operators for selected dates
  useEffect(() => {
    const fetchAvailableOperators = async () => {
      if (selectedDates.length === 0) {
        // If no dates selected, show all operators
        if (operatorsData) {
          setAvailableOperatorIds(new Set(operatorsData.map(op => op.id)));
        }
        return;
      }

      try {
        // Get available operators for each date and find intersection
        const operatorSets = await Promise.all(
          selectedDates.map(async (date) => {
            const response = await axios.get(`/operator/available?date=${date}`);
            const data = response.data;
            const availableOps = (data.availableOperators || []) as Array<{ 
              Id?: number; 
              id?: number;
              Name?: string; 
              name?: string; 
              Code?: string; 
              code?: string;
            }>;
            // Map operator names to IDs from operatorsData
            const operatorIdSet = new Set<number>();
            availableOps.forEach(availOp => {
              const opId = availOp.Id ?? availOp.id;
              const opName = availOp.Name ?? availOp.name;
              const opCode = availOp.Code ?? availOp.code;
              
              if (opId) {
                // If ID is directly available, use it
                operatorIdSet.add(opId);
              } else if (opName || opCode) {
                // Otherwise, find by name or code
                const matchingOp = operatorsData?.find(op => 
                  (opName && op.name === opName) || (opCode && op.code === opCode)
                );
                if (matchingOp) {
                  operatorIdSet.add(matchingOp.id);
                }
              }
            });
            return operatorIdSet;
          })
        );

        // Find operators available for ALL selected dates
        if (operatorSets.length > 0) {
          let intersection = operatorSets[0];
          for (let i = 1; i < operatorSets.length; i++) {
            const currentSet = operatorSets[i];
            intersection = new Set<number>(
              Array.from(intersection).filter((opId: number) => currentSet.has(opId))
            );
          }
          setAvailableOperatorIds(intersection);
        } else {
          setAvailableOperatorIds(new Set());
        }
      } catch (error) {
        console.error('Error fetching available operators:', error);
        // On error, show all operators
        if (operatorsData) {
          setAvailableOperatorIds(new Set(operatorsData.map(op => op.id)));
        }
      }
    };

    fetchAvailableOperators();
  }, [selectedDates, operatorsData]);

  // Clear selected operators that are no longer available
  useEffect(() => {
    if (availableOperatorIds.size > 0) {
      const validOperators = selectedOperators.filter(opId => availableOperatorIds.has(opId));
      if (validOperators.length !== selectedOperators.length) {
        setSelectedOperators(validOperators);
      }
    }
  }, [availableOperatorIds, selectedOperators]);



  // Fetch wallet balance
  const { data: walletData } = useQuery({
    queryKey: ['wallet', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const response = await axios.get(`/wallet/user/${user.id}`);
      return response.data;
    },
    enabled: !!user?.id,
    refetchInterval: 30000, // Refresh every 30 seconds
  });



  const handleOperatorToggle = (operatorId: number) => {
    if (selectedOperators.includes(operatorId)) {
      setSelectedOperators(selectedOperators.filter(id => id !== operatorId));
    } else {
      setSelectedOperators([...selectedOperators, operatorId]);
    }
  };

  const handleDateToggle = (date: string) => {
    setSelectedDates(prev => {
      if (prev.includes(date)) {
        return prev.filter(d => d !== date);
      } else {
        return [...prev, date];
      }
    });
  };

  const handleAddToBet = () => {
    const requiredLength = getRequiredNumberLength();
    
    // Validation: must have operator and date selected
    if (selectedOperators.length === 0 || selectedDates.length === 0) {
      return; // Don't proceed if validation fails
    }
    
    if (numberInput.length === requiredLength) {
      // Get selected operator names from the operators API data
      const selectedOperatorNames = selectedOperators.map(opId => {
        const operatorData = operatorsData?.find(operator => operator.id === opId);
        return operatorData ? operatorData.name : `Operator ${opId}`;
      });
      
      // Use selected dates
      const datesToUse = selectedDates;

      // Create bet entry with selected operators and dates
      const updatedEntries = [...betEntries];
      
      // Create separate bet entries for each selected operator
      selectedOperatorNames.forEach(operatorName => {
        const counterBets: CounterBet[] = [{
          code: String(operatorName),
          bigAmount: bigAmount,
          smallAmount: smallAmount,
          fourAAmount: fourAAmount,
          aAmount: aAmount,
          abcAmount: abcAmount,
          fiveDAmount: fiveDAmount,
          sixDAmount: sixDAmount
        }];
        
        // Check if there's already an entry with the same number, dates, operator, and bet amounts
        const existingEntryIndex = updatedEntries.findIndex(entry => 
          entry.numbers[0] === numberInput &&
          JSON.stringify(entry.dates.sort()) === JSON.stringify(datesToUse.sort()) &&
          entry.operatorName === operatorName &&
          entry.optionType === optionType &&
          entry.bigAmount === bigAmount &&
          entry.smallAmount === smallAmount &&
          entry.fourAAmount === fourAAmount &&
          entry.aAmount === aAmount &&
          entry.abcAmount === abcAmount &&
          entry.fiveDAmount === fiveDAmount &&
          entry.sixDAmount === sixDAmount
        );
        
        if (existingEntryIndex >= 0) {
          // Update existing entry
          updatedEntries[existingEntryIndex] = {
            ...updatedEntries[existingEntryIndex],
            counterBets
          };
        } else {
          // Determine game type based on bet amounts
          let gameType = '4D';
          if (sixDAmount > 0) {
            gameType = '6D';
          } else if (fiveDAmount > 0) {
            gameType = '5D';
          } else if (aAmount > 0 || abcAmount > 0) {
            gameType = '3D';
          }
          
          // Create new entry with simplified structure
          const newEntry: BetEntry = {
            id: Date.now() + Math.random(), // Ensure unique IDs
            gameType: gameType,
            numbers: [numberInput],
            betType: selectedBetType,
            optionType: optionType,
            bigAmount: bigAmount,
            smallAmount: smallAmount,
            fourAAmount: fourAAmount,
            aAmount: aAmount,
            abcAmount: abcAmount,
            fiveDAmount: fiveDAmount,
            sixDAmount: sixDAmount,
            dates: datesToUse, // Include all selected dates in one entry
            operatorName: operatorName, // Simplified: just operator name
            counterBets: counterBets
          };
          updatedEntries.push(newEntry);
        }
      });
      
      setBetEntries(updatedEntries);
      setNumberInput('');
      // Keep selected operators and dates for easier consecutive betting
      
      // setSelectedOperators([]);
    }
  };



  const handleQuickPick = () => {
    const requiredLength = getRequiredNumberLength();
    const maxNumber = Math.pow(10, requiredLength);
    const randomNumber = Math.floor(Math.random() * maxNumber).toString().padStart(requiredLength, '0');
    setNumberInput(randomNumber);
  };

  const handleRemoveBetEntry = (id: number) => {
    setBetEntries(betEntries.filter(entry => entry.id !== id));
  };

  // Calculate number of permutations for box betting
  const calculatePermutations = (number: string): number => {
    const digits = number.split('');
    const uniqueDigits = Array.from(new Set(digits));
    
    if (uniqueDigits.length === 4) {
      // All different digits: 4! = 24 permutations
      return 24;
    } else if (uniqueDigits.length === 3) {
      // One pair: 4!/2! = 12 permutations
      return 12;
    } else if (uniqueDigits.length === 2) {
      // Check if it's two pairs or triple
      const digitCounts = digits.reduce((acc, digit) => {
        acc[digit] = (acc[digit] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);
      
      const counts = Object.values(digitCounts);
      if (counts.includes(3)) {
        // One triple, one single: 4!/3! = 4 permutations
        return 4;
      } else {
        // Two pairs: 4!/(2!*2!) = 6 permutations
        return 6;
      }
    } else {
      // All same digits: 1 permutation
      return 1;
    }
  };

  const calculateBaseTotalAmount = () => {
    return betEntries.reduce((total, entry) => {
      let multiplier = 1;
      if (entry.optionType === 'box') {
        multiplier = calculatePermutations(entry.numbers[0]);
      } else if (entry.optionType === 'reverse') {
        multiplier = 2; // x2 for reverse bets
      }
      
      // Base total: just the bet amount per counter without multiplying by number of counters or dates
      const baseAmount = entry.counterBets.length > 0 ? 
        (entry.counterBets[0].bigAmount + entry.counterBets[0].smallAmount + 
         entry.counterBets[0].fourAAmount + entry.counterBets[0].aAmount + 
         entry.counterBets[0].abcAmount + entry.counterBets[0].fiveDAmount + 
         entry.counterBets[0].sixDAmount) * multiplier :
        0;
      return total + baseAmount;
    }, 0);
  };

  const calculateTotalAmount = () => {
    return betEntries.reduce((total, entry) => {
      let multiplier = 1;
      if (entry.optionType === 'box') {
        multiplier = calculatePermutations(entry.numbers[0]);
      } else if (entry.optionType === 'reverse') {
        multiplier = 2; // x2 for reverse bets
      }
      
      const counterTotal = entry.counterBets.reduce((counterSum, counterBet) => {
        return counterSum + (counterBet.bigAmount + counterBet.smallAmount + 
                           counterBet.fourAAmount + counterBet.aAmount + 
                           counterBet.abcAmount + counterBet.fiveDAmount + 
                           counterBet.sixDAmount);
      }, 0);
      
      const entryTotal = counterTotal * entry.dates.length * multiplier;
      return total + entryTotal;
    }, 0);
  };

  // Removed old date and counter toggle functions - now using handleGameDrawToggle

  const placeBetMutation = useMutation({
    mutationFn: async (entries: BetEntry[]) => {
      console.log('🚀 placeBetMutation started with entries:', entries);
      const bets: any[] = [];
      
      for (const entry of entries) {
        // Calculate total amount for this entry
        const totalAmount = entry.bigAmount + entry.smallAmount + entry.fourAAmount + 
                           entry.aAmount + entry.abcAmount + entry.fiveDAmount + entry.sixDAmount;
        
        console.log('💰 Entry total amount:', totalAmount, 'for entry:', entry);
        
        if (totalAmount <= 0) {
          console.log('⚠️ Skipping entry with zero amount');
          continue; // Skip entries with no amount
        }
        
        // Create bet objects for each bet type with amount (simplified structure)
        const betTypes = [
          { type: 'Big', amount: entry.bigAmount },
          { type: 'Small', amount: entry.smallAmount },
          { type: '4A', amount: entry.fourAAmount },
          { type: 'A', amount: entry.aAmount },
          { type: 'ABC', amount: entry.abcAmount },
          { type: '5D', amount: entry.fiveDAmount },
          { type: '6D', amount: entry.sixDAmount }
        ];
        
        // Helper: map UI play to OptionType for backend
        const optionTypeValue = ((): string => {
          switch (entry.optionType) {
            case 'ibox': return 'iBox';
            case 'box': return 'Box';
            case 'reverse': return 'Reverse';
            default: return 'Single';
          }
        })();

        // Process each date for this entry
        entry.dates.forEach(date => {
          // Create a single bet object with all amounts for this date
          if (totalAmount > 0) {
            const reverseMultiplier = optionTypeValue === 'Reverse' ? 2 : 1;

            const baseBet = {
              OperatorName: entry.operatorName,
              DrawDate: date,
              Number: entry.numbers.join(''),
              OptionType: optionTypeValue,
              BigAmount: entry.bigAmount * reverseMultiplier,
              SmallAmount: entry.smallAmount * reverseMultiplier,
              FourAAmount: entry.fourAAmount * reverseMultiplier,
              AAmount: entry.aAmount * reverseMultiplier,
              AbcAmount: entry.abcAmount * reverseMultiplier,
              FiveDAmount: entry.fiveDAmount * reverseMultiplier,
              SixDAmount: entry.sixDAmount * reverseMultiplier
            };

            const bet = {
              ...baseBet,
              BetType: 'Big' // Use a valid bet type for validation
            };

            bets.push(bet);
          }
        });
      }
      
      // Place bets using the correct endpoint
      console.log('📋 Final bets to send:', bets);
      const betData = { Bets: bets };
      console.log('📤 Sending bet data:', betData);
      
      const response = await axios.post('/bet/place-multiple', betData);
      console.log('✅ Bet placement response:', response.data);
      
      console.log('🎉 All bets placed successfully:', response.data);
      return response.data;
    },
    onSuccess: (data) => {
      console.log('✅ placeBetMutation onSuccess:', data);
      setBetEntries([]);
      // Keep selected operators and dates for easier consecutive betting
      
      // setSelectedOperators([]);
      setConfirmDialog(false);
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
      // Invalidate wallet query to update balance immediately
      queryClient.invalidateQueries({ queryKey: ['wallet'] });
    },
    onError: (error: any) => {
      console.error('❌ placeBetMutation onError:', error);
      const errorData = error.response?.data;
      let errorMessage = 'Failed to place bet: ' + (errorData?.message || error.message);
      
      // Show specific validation errors if available
      if (errorData?.errors && Array.isArray(errorData.errors)) {
        errorMessage += '\n\nValidation errors:\n' + errorData.errors.join('\n');
      }
      
      // Show credit information if available
      if (errorData?.availableCredit !== undefined) {
        errorMessage += `\n\nAvailable credit: ${errorData.availableCredit}`;
      }
      
      alert(errorMessage);
    }
  });

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
          <CasinoIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Lottery Betting
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Chip
            icon={<WalletIcon />}
            label={`Balance: $${user?.credits?.toFixed(2) || '0.00'}`}
            color="primary"
            variant="outlined"
          />
        </Box>
      </Box>

      {/* Operator Selection */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
          Select Operator
        </Typography>
        
        {selectedDates.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Please select at least one date to see available operators.
          </Alert>
        )}
        
        {selectedDates.length > 0 && availableOperatorIds.size === 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No operators are available for betting on the selected date(s). 
            Regular operators require a special draw on Monday/Tuesday/Thursday/Friday.
          </Alert>
        )}
        
        {selectedDates.length > 0 && availableOperatorIds.size > 0 && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {availableOperatorIds.size} operator(s) available for the selected date(s)
          </Alert>
        )}
        
        {isLoadingOperators ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <Typography>Loading operators...</Typography>
          </Box>
        ) : operatorsError ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <Typography color="error">Failed to load operators</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {operatorsData
              ?.filter((operator) => {
                // If no dates selected, show all operators
                if (selectedDates.length === 0) return true;
                // Otherwise, only show available operators
                return availableOperatorIds.has(operator.id);
              })
              .map((operator) => {
                const isSelected = selectedOperators.includes(operator.id);
                
                return (
                  <FormControlLabel
                    key={operator.id}
                    control={
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleOperatorToggle(operator.id)}
                        size="small"
                      />
                    }
                    label={
                      <Chip 
                        label={operator.name || 'Unknown'}
                        size="small"
                        color={isSelected ? 'primary' : 'default'}
                        variant={isSelected ? 'filled' : 'outlined'}
                      />
                    }
                    sx={{ m: 0 }}
                  />
                );
              })}
          </Box>
        )}
      </Paper>

      {/* Date Selection */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
          Select Draw Dates (Next 7 Days)
        </Typography>
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {availableDates.map((date) => {
            const isSelected = selectedDates.includes(date);
            // Parse date string as local date (not UTC)
            const [year, month, day] = date.split('-').map(Number);
            const dateObj = new Date(year, month - 1, day);
            // Check if this is today (using local timezone)
            const isToday = date === getTodayString();
            
            return (
              <FormControlLabel
                key={date}
                control={
                  <Checkbox
                    checked={isSelected}
                    onChange={() => handleDateToggle(date)}
                    size="small"
                  />
                }
                label={
                  <Chip 
                    label={`${dateObj.toLocaleDateString('en-US', { 
                      weekday: 'short', 
                      month: 'short', 
                      day: 'numeric' 
                    })}${isToday ? ' (Today)' : ''}`}
                    size="small"
                    color={isSelected ? 'primary' : 'default'}
                    variant={isSelected ? 'filled' : 'outlined'}
                  />
                }
                sx={{ m: 0 }}
              />
            );
          })}
        </Box>
      </Paper>

      {/* Main Panel - Number Input and Bet Amount */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          4D Number & Bet Amount
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          💡 Tip: Enter amounts only for the bet types you want to play. Each number creates one bet record with all your selected amounts.
        </Typography>
        {(aAmount > 0 || abcAmount > 0 || fiveDAmount > 0 || sixDAmount > 0) && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {sixDAmount > 0 ? '🎲 6D bet selected: Please enter 6 digits' : 
             fiveDAmount > 0 ? '🎲 5D bet selected: Please enter 5 digits' : 
             '🎲 3D bet selected: Please enter 3 digits'}
          </Alert>
        )}
        
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'end' }}>
          {/* Number Input */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {sixDAmount > 0 ? '6D Number' : fiveDAmount > 0 ? '5D Number' : (aAmount > 0 || abcAmount > 0) ? '3D Number' : '4D Number'}
            </Typography>
            <TextField
              fullWidth
              value={numberInput}
              onChange={(e) => {
                 const value = e.target.value.replace(/\D/g, ''); // Only allow digits
                 const maxLength = getRequiredNumberLength();
                 if (value.length <= maxLength) {
                   setNumberInput(value);
                 }
               }}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && numberInput.length === getRequiredNumberLength()) {
                  handleAddToBet();
                }
              }}
              placeholder={`Enter ${getRequiredNumberLength()} digits`}
              inputProps={{ 
                maxLength: getRequiredNumberLength(),
                style: { fontSize: '1.2rem', textAlign: 'center' }
              }}
              size="small"
            />
          </Box>
          
          {/* Options Dropdown */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Options
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={optionType}
                onChange={(e) => setOptionType(e.target.value)}
                displayEmpty
              >
                <MenuItem value="single">Single</MenuItem>
                <MenuItem value="ibox">iBox</MenuItem>
                <MenuItem value="box">Box</MenuItem>
                <MenuItem value="reverse">Reverse</MenuItem>
              </Select>
            </FormControl>
          </Box>
          
          {/* BIG Amount */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ mb: 1, color: '#e91e63' }}>
              BIG Amount
            </Typography>
            <TextField
              fullWidth
              type="text"
              value={bigAmount === 0 ? '' : bigAmount.toString()}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, ''); // Only allow digits
                setBigAmount(value === '' ? 0 : Number(value));
              }}
              placeholder="0"
              inputProps={{ 
                style: { textAlign: 'center' }
              }}
              size="small"
            />
          </Box>
          
          {/* SMALL Amount */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ mb: 1, color: '#2196f3' }}>
              SMALL Amount
            </Typography>
            <TextField
              fullWidth
              type="text"
              value={smallAmount === 0 ? '' : smallAmount.toString()}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, ''); // Only allow digits
                setSmallAmount(value === '' ? 0 : Number(value));
              }}
              placeholder="0"
              inputProps={{ 
                style: { textAlign: 'center' }
              }}
              size="small"
            />
          </Box>
          
          {/* 4A Amount */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ mb: 1, color: '#ff9800' }}>
              4A Amount (3 numbers)
            </Typography>
            <TextField
              fullWidth
              type="text"
              value={fourAAmount === 0 ? '' : fourAAmount.toString()}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, ''); // Only allow digits
                setFourAAmount(value === '' ? 0 : Number(value));
              }}
              placeholder="0"
              inputProps={{ 
                style: { textAlign: 'center' }
              }}
              size="small"
            />
          </Box>
          
          {/* A Amount */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ mb: 1, color: '#4caf50' }}>
              A Amount (3 numbers)
            </Typography>
            <TextField
              fullWidth
              type="text"
              value={aAmount === 0 ? '' : aAmount.toString()}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, ''); // Only allow digits
                setAAmount(value === '' ? 0 : Number(value));
              }}
              placeholder="0"
              inputProps={{ 
                style: { textAlign: 'center' }
              }}
              size="small"
            />
          </Box>
          
          {/* ABC Amount */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ mb: 1, color: '#9c27b0' }}>
              ABC Amount (3 numbers)
            </Typography>
            <TextField
              fullWidth
              type="text"
              value={abcAmount === 0 ? '' : abcAmount.toString()}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, ''); // Only allow digits
                setAbcAmount(value === '' ? 0 : Number(value));
              }}
              placeholder="0"
              inputProps={{ 
                style: { textAlign: 'center' }
              }}
              size="small"
            />
          </Box>
          
          {/* 5D Amount */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ mb: 1, color: '#795548' }}>
              5D Amount (5 numbers)
            </Typography>
            <TextField
              fullWidth
              type="text"
              value={fiveDAmount === 0 ? '' : fiveDAmount.toString()}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, ''); // Only allow digits
                setFiveDAmount(value === '' ? 0 : Number(value));
              }}
              placeholder="0"
              inputProps={{ 
                style: { textAlign: 'center' }
              }}
              size="small"
            />
          </Box>
          
          {/* 6D Amount */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ mb: 1, color: '#607d8b' }}>
              6D Amount (6 numbers)
            </Typography>
            <TextField
              fullWidth
              type="text"
              value={sixDAmount === 0 ? '' : sixDAmount.toString()}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, ''); // Only allow digits
                setSixDAmount(value === '' ? 0 : Number(value));
              }}
              placeholder="0"
              inputProps={{ 
                style: { textAlign: 'center' }
              }}
              size="small"
            />
          </Box>
          
          {/* Total Amount Display */}
          <Box sx={{ flex: 1.5 }}>
            <Typography variant="body2" sx={{ mb: 1, color: 'primary.main' }}>
              Total Amount
            </Typography>
            <Box sx={{ 
              height: '40px', 
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'center',
              backgroundColor: 'transparent'
            }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary', fontSize: '0.75rem' }}>
                Base: ${numberInput.length === getRequiredNumberLength() ? ((bigAmount + smallAmount + fourAAmount + aAmount + abcAmount + fiveDAmount + sixDAmount) * (optionType === 'box' ? calculatePermutations(numberInput) : optionType === 'reverse' ? 2 : 1)).toFixed(2) : '0.00'}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main', fontSize: '0.85rem' }}>
                Final: ${numberInput.length === getRequiredNumberLength() ? ((bigAmount + smallAmount + fourAAmount + aAmount + abcAmount + fiveDAmount + sixDAmount) * (selectedOperators.length > 0 ? selectedOperators.length : 1) * (optionType === 'box' ? calculatePermutations(numberInput) : optionType === 'reverse' ? 2 : 1)).toFixed(2) : '0.00'}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Validation Alert */}
      {(selectedOperators.length === 0 || selectedDates.length === 0) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {selectedOperators.length === 0 && selectedDates.length === 0 
            ? 'Please select at least one operator and one date before adding to bet slip.'
            : selectedOperators.length === 0
            ? 'Please select at least one operator before adding to bet slip.'
            : 'Please select at least one date before adding to bet slip.'}
        </Alert>
      )}

      {/* Add to Bet Button */}
       <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
         <Button
           fullWidth
           variant="contained"
           size="large"
           startIcon={<AddIcon />}
           onClick={handleAddToBet}
           disabled={
              numberInput.length !== getRequiredNumberLength() ||
              (bigAmount <= 0 && smallAmount <= 0 && fourAAmount <= 0 && aAmount <= 0 && abcAmount <= 0 && fiveDAmount <= 0 && sixDAmount <= 0) ||
              selectedOperators.length === 0 ||
              selectedDates.length === 0
           }
         >
           Add to Bet Slip
         </Button>
         <Button
           variant="outlined"
           size="large"
           startIcon={<ClearIcon />}
           onClick={() => {
             setSelectedOperators([]);
             setSelectedDates([]);
           }}
           disabled={selectedOperators.length === 0}
           sx={{ minWidth: 'auto', px: 2 }}
         >
           Clear
         </Button>
       </Box>

      {/* Bet Slip */}
      {betEntries.length > 0 && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            Bet Slip ({betEntries.length} entries)
          </Typography>
          <TableContainer sx={{ maxHeight: 300 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ py: 1 }}>Game</TableCell>
                  <TableCell sx={{ py: 1 }}>Numbers</TableCell>
                  <TableCell sx={{ py: 1 }}>Play Type</TableCell>
                  <TableCell sx={{ py: 1 }}>BIG</TableCell>
                  <TableCell sx={{ py: 1 }}>SMALL</TableCell>
                  <TableCell sx={{ py: 1 }}>4A</TableCell>
                  <TableCell sx={{ py: 1 }}>A</TableCell>
                  <TableCell sx={{ py: 1 }}>ABC</TableCell>
                  <TableCell sx={{ py: 1 }}>5D</TableCell>
                  <TableCell sx={{ py: 1 }}>6D</TableCell>
                  <TableCell sx={{ py: 1 }}>Counters</TableCell>
                  <TableCell sx={{ py: 1 }}>Dates</TableCell>
                  <TableCell sx={{ py: 1 }}>Total</TableCell>
                  <TableCell sx={{ py: 1 }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(() => {
                  // Group entries by numbers, dates, bet amounts, and play type
                  const groupedEntries = betEntries.reduce((groups, entry) => {
                    const key = `${entry.numbers.join('')}-${entry.dates.sort().join(',')}-${entry.optionType}-${entry.bigAmount}-${entry.smallAmount}-${entry.fourAAmount}-${entry.aAmount}-${entry.abcAmount}-${entry.fiveDAmount}-${entry.sixDAmount}`;
                    if (!groups[key]) {
                      groups[key] = [];
                    }
                    groups[key].push(entry);
                    return groups;
                  }, {} as Record<string, BetEntry[]>);

                  return Object.values(groupedEntries).map((groupEntries) => {
                    const firstEntry = groupEntries[0];
                    let multiplier = 1;
                    if (firstEntry.optionType === 'box') {
                      multiplier = calculatePermutations(firstEntry.numbers[0]);
                    } else if (firstEntry.optionType === 'reverse') {
                      multiplier = 2;
                    }
                    
                    const counterTotal = firstEntry.counterBets.reduce((counterSum, counterBet) => {
                      return counterSum + (counterBet.bigAmount + counterBet.smallAmount + 
                                         counterBet.fourAAmount + counterBet.aAmount + 
                                         counterBet.abcAmount + counterBet.fiveDAmount + 
                                         counterBet.sixDAmount);
                    }, 0);
                    
                    // Calculate total for all operators in this group
                    const entryTotal = counterTotal * firstEntry.dates.length * multiplier * groupEntries.length;
                    
                    return (
                      <TableRow key={`group-${firstEntry.numbers.join('')}-${firstEntry.dates.join(',')}`} sx={{ '& td': { py: 0.5 } }}>
                        <TableCell>{firstEntry.gameType}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontWeight: 'bold', fontSize: '1rem' }}>
                          {firstEntry.numbers.join('')}
                        </TableCell>
                        <TableCell>
                          {optionTypeLabels[firstEntry.optionType] ?? firstEntry.optionType}
                          {(firstEntry.optionType === 'box' || firstEntry.optionType === 'reverse') && (
                            <Typography variant="caption" sx={{ display: 'inline', color: 'primary.main', ml: 1 }}>
                              ({multiplier}x)
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {firstEntry.bigAmount > 0 ? (
                            <Typography sx={{ color: '#f44336', fontWeight: 'bold' }}>
                              ${firstEntry.bigAmount}
                            </Typography>
                          ) : (
                            <Typography color="text.disabled">-</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {firstEntry.smallAmount > 0 ? (
                            <Typography sx={{ color: '#4caf50', fontWeight: 'bold' }}>
                              ${firstEntry.smallAmount}
                            </Typography>
                          ) : (
                            <Typography color="text.disabled">-</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {firstEntry.fourAAmount > 0 ? (
                            <Typography sx={{ color: '#ff9800', fontWeight: 'bold' }}>
                              ${firstEntry.fourAAmount}
                            </Typography>
                          ) : (
                            <Typography color="text.disabled">-</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {firstEntry.aAmount > 0 ? (
                            <Typography sx={{ color: '#4caf50', fontWeight: 'bold' }}>
                              ${firstEntry.aAmount}
                            </Typography>
                          ) : (
                            <Typography color="text.disabled">-</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {firstEntry.abcAmount > 0 ? (
                            <Typography sx={{ color: '#9c27b0', fontWeight: 'bold' }}>
                              ${firstEntry.abcAmount}
                            </Typography>
                          ) : (
                            <Typography color="text.disabled">-</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {firstEntry.fiveDAmount > 0 ? (
                            <Typography sx={{ color: '#795548', fontWeight: 'bold' }}>
                              ${firstEntry.fiveDAmount}
                            </Typography>
                          ) : (
                            <Typography color="text.disabled">-</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {firstEntry.sixDAmount > 0 ? (
                            <Typography sx={{ color: '#607d8b', fontWeight: 'bold' }}>
                              ${firstEntry.sixDAmount}
                            </Typography>
                          ) : (
                            <Typography color="text.disabled">-</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {/* Display all operator names in this group */}
                            {groupEntries.map((entry, index) => {
                              const operatorName = entry.operatorName;
                              // Try to find exact match first, then partial match
                              let counter = lotteryCounters.find(c => c.name === operatorName);
                              if (!counter) {
                                // Try partial matching for common operator names
                                counter = lotteryCounters.find(c => 
                                   (operatorName.toLowerCase().includes('magnum') && c.name.includes('Magnum')) ||
                                   (operatorName.toLowerCase().includes('toto') && c.name.includes('Toto')) ||
                                   (operatorName.toLowerCase().includes('damacai') && c.name.includes('Damacai')) ||
                                   (operatorName.toLowerCase().includes('singapore') && c.name.includes('Singapore'))
                                 );
                              }
                              return (
                                <Typography
                                  key={`${entry.id}-${index}`}
                                  variant="body2"
                                  sx={{ 
                                    color: counter?.color || '#666',
                                    fontWeight: 'bold',
                                    fontSize: '0.875rem'
                                  }}
                                >
                                  {operatorName}
                                </Typography>
                              );
                            })}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
                              {firstEntry.dates.map(date => {
                                const dateObj = new Date(date);
                                return dateObj.toLocaleDateString('en-US', { 
                                  month: 'short', 
                                  day: 'numeric' 
                                });
                              }).join(', ')}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                          ${entryTotal.toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Remove bet">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                // Remove all entries in this group
                                groupEntries.forEach(entry => handleRemoveBetEntry(entry.id));
                              }}
                              sx={{ p: 0.5 }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  });
                })()}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Divider sx={{ my: 2 }} />
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5 }}>
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                Base Total: ${calculateBaseTotalAmount().toFixed(2)}
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                Final Total: ${calculateTotalAmount().toFixed(2)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setBetEntries([]);
                  setSelectedOperators([]);
                  setSelectedDates([]);
                }}
              >
                Clear All
              </Button>
              <Button
                variant="contained"
                size="large"
                onClick={() => setConfirmDialog(true)}
                disabled={calculateTotalAmount() > (walletData?.balance || 0)}
              >
                Place Bets (${calculateTotalAmount().toFixed(2)})
              </Button>
            </Box>
          </Box>
          
          {calculateTotalAmount() > (walletData?.balance || 0) && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Insufficient balance. You need ${(calculateTotalAmount() - (walletData?.balance || 0)).toFixed(2)} more.
            </Alert>
          )}
        </Paper>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Confirm Bet Placement</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            You are about to place {betEntries.length} bet(s) for a total of ${calculateTotalAmount().toFixed(2)}.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please review your selections carefully. Once placed, bets cannot be cancelled.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => placeBetMutation.mutate(betEntries)}
            disabled={placeBetMutation.isPending}
          >
            {placeBetMutation.isPending ? 'Placing...' : 'Confirm & Place Bets'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EnhancedBetPage;